import React, { useMemo } from 'react';
import { View, Text, Pressable, Platform } from 'react-native';
import { Clock, Trophy, ChevronRight } from 'lucide-react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import type { CompetitionSummary } from '../../../src/competitions/types/competitions.types';
import { CompetitionCrest } from '../CompetitionCrest/CompetitionCrest';
import {
  HUB_JOIN_HINT,
  HUB_FINISHED_META_PREFIX,
  HUB_STATUS_LIVE_LABEL,
  HUB_STATUS_UPCOMING_ACCESSIBILITY,
  HUB_STATUS_CLOCK_ICON_SIZE,
  HUB_CREST_SIZE,
  HUB_CREST_SIZE_COMPACT,
  HUB_CREST_LOGO_SIZE,
  HUB_CREST_LOGO_SIZE_COMPACT,
  HUB_TEST_IDS,
} from './CompetitionsHub.constants';
import { getCompetitionCardStyles } from './CompetitionsHub.styles';

interface CompetitionCardProps {
  competition: CompetitionSummary;
  /** Null when this card has no destination yet — renders non-pressable. */
  onPress: (() => void) | null;
  /** Compact rows for the Past/archive section. */
  compact?: boolean;
}

function buildMetaLine(competition: CompetitionSummary): string {
  const { lifecycle, viewer } = competition;
  if (viewer.isParticipant) {
    const base = `Your points: ${viewer.totalPoints} · Squads: ${viewer.squadCount}`;
    return lifecycle === 'LIVE' || lifecycle === 'UPCOMING' ? base : `${HUB_FINISHED_META_PREFIX} · ${base}`;
  }
  return competition.description ?? '';
}

/** Sport-tinted monogram color so mixed-sport lists read at a glance. */
function sportTint(theme: ReturnType<typeof useTheme>, sport: string): string {
  if (sport === 'BASEBALL') return theme.error;
  if (sport === 'BASKETBALL') return theme.warning;
  return theme.accent;
}

export const CompetitionCard: React.FC<CompetitionCardProps> = ({ competition, onPress, compact = false }) => {
  const theme = useTheme();
  const s = useMemo(() => getCompetitionCardStyles(theme), [theme]);

  const { lifecycle } = competition;
  const isLive = lifecycle === 'LIVE';
  const isPast = !isLive && lifecycle !== 'UPCOMING';
  const isUpcoming = lifecycle === 'UPCOMING';
  const displayName = competition.brand?.name ?? competition.name;
  const metaLine = buildMetaLine(competition);
  const testID = HUB_TEST_IDS.card(competition.id);

  const body = (
    <>
      <CompetitionCrest
        logoUrl={competition.logoUrl}
        name={displayName}
        shortName={competition.brand?.shortName}
        flagCode={competition.brand?.flagCode}
        size={compact ? HUB_CREST_SIZE_COMPACT : HUB_CREST_SIZE}
        logoSize={compact ? HUB_CREST_LOGO_SIZE_COMPACT : HUB_CREST_LOGO_SIZE}
        monogramColor={sportTint(theme, competition.sport)}
        style={s.logoCircle}
      />
      <View style={s.content}>
        <View style={s.nameRow}>
          <Text style={[s.name, compact && s.nameCompact]} numberOfLines={1}>{displayName}</Text>
        </View>
        {metaLine !== '' && (
          <Text style={[s.metaLine, compact && s.metaLineCompact]} numberOfLines={compact ? 1 : 2}>
            {metaLine}
          </Text>
        )}
        {onPress != null && isLive && !competition.viewer.isParticipant && (
          <Text style={s.joinHint}>{HUB_JOIN_HINT}</Text>
        )}
      </View>
      <View style={s.trailing}>
        {/* The state, never a date — see Story 16. The clock carries a label because it has no text. */}
        {isUpcoming && (
          <View
            style={s.statusPill}
            testID={HUB_TEST_IDS.statusUpcoming(competition.id)}
            accessibilityRole="image"
            accessibilityLabel={HUB_STATUS_UPCOMING_ACCESSIBILITY}
          >
            <Clock size={HUB_STATUS_CLOCK_ICON_SIZE} color={theme.accent} />
          </View>
        )}
        {isLive && (
          <View style={s.statusPill} testID={HUB_TEST_IDS.statusLive(competition.id)}>
            <View style={s.liveDot} />
            <Text style={s.statusLiveLabel}>{HUB_STATUS_LIVE_LABEL}</Text>
          </View>
        )}
        {isPast && <Trophy size={16} color={theme.accent} />}
        {onPress != null && <ChevronRight size={18} color={theme.gray[400]} />}
      </View>
    </>
  );

  const staticStyle = [s.card, isUpcoming && s.cardUpcoming, isPast && s.cardPast, compact && s.cardCompact];

  if (onPress == null) {
    return (
      <View style={staticStyle} testID={testID}>
        {body}
      </View>
    );
  }
  return (
    <Pressable
      style={({ pressed, hovered }) => [
        ...staticStyle,
        Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
        pressed && s.cardPressed,
        hovered && Platform.OS === 'web' && s.cardHover,
      ]}
      onPress={onPress}
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={displayName}
    >
      {body}
    </Pressable>
  );
};
